import React, { useMemo } from 'react'
import { Box, Tooltip, IconButton } from '@mui/material'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { ChartThemeManager, type ChartTheme } from '../Services/ChartThemeManager'
import type { LegendItem } from './LegendCompact'

export interface LegendStripProps {
  items: LegendItem[]
  theme?: ChartTheme
  onItemClick?: (item: LegendItem) => void
  onToggleVisibility?: (item: LegendItem) => void
  className?: string
  style?: React.CSSProperties
}

/**
 * LegendStrip: bottom legend style with chip-like items, wrapping across rows.
 * - Row click toggles visibility (dims label when hidden)
 * - Gear icon opens configuration callback without toggling
 * - Keyboard accessible (Enter/Space)
 */
export const LegendStrip: React.FC<LegendStripProps> = ({
  items,
  theme = 'dark',
  onItemClick,
  onToggleVisibility,
  className,
  style
}) => {
  const colors = ChartThemeManager.getChartColors(theme)
  const styles = ChartThemeManager.getChartStyles()

  const legendItems = useMemo(() => items.filter(i => i.name), [items])

  return (
    <Box
      className={className}
      style={style}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        rowGap: 1,
        alignItems: 'center',
        px: 1,
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'transparent'
      }}
      data-testid="legend-strip"
    >
      {legendItems.map((item, idx) => (
        <Box
          key={`${item.name}-${idx}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: 999,
            border: '1px solid',
            borderColor: 'divider',
            px: 1,
            py: 0.5,
            backgroundColor: 'transparent'
          }}
        >
          <Box
            role={onToggleVisibility ? 'button' : undefined}
            tabIndex={onToggleVisibility ? 0 : undefined}
            onClick={onToggleVisibility ? () => onToggleVisibility(item) : undefined}
            onKeyDown={onToggleVisibility ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleVisibility(item) } } : undefined}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: onToggleVisibility ? 'pointer' : 'default' }}
            data-testid="legend-strip-chip"
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                backgroundColor: item.color || colors.text.primary,
                display: 'inline-block'
              }}
            />
            <Tooltip title={`${item.name}${item.yAxisId ? ` • ${item.yAxisId}` : ''}`}>
              <span style={{ fontSize: styles.fontSize.small, whiteSpace: 'nowrap', opacity: item.visible === false ? 0.35 : 1 }}>{item.name}</span>
            </Tooltip>
          </Box>
          {onItemClick && (
            <IconButton
              size="small"
              aria-label={`Configure ${item.name}`}
              onClick={(e) => { e.stopPropagation(); onItemClick(item) }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onItemClick(item) } }}
              sx={{ color: colors.text.primary, opacity: 0.9 }}
            >
              <SettingsOutlinedIcon fontSize="inherit" />
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  )
}

export default LegendStrip

